import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook } from '@fortawesome/free-brands-svg-icons/faFacebook';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons/faChevronUp';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import {
  UserService,
  UserPlanService,
  SitterService,
  SitterNotificationSettingsService,
  GoogleConnectionService,
  SitterPlanService,
  ValidationService,
} from 'us-web-services';
import { Checkbox } from 'us-react-components/dist/Checkbox/Checkbox';
import { ConfirmModal } from 'us-react-components/dist/ConfirmModal/ConfirmModal';
import { PasswordChangeForm } from 'us-react-components/dist/PasswordChangeForm/PasswordChangeForm';
import { tests } from 'us-react-components/dist/constants/Tests';
import { getLongDateString } from 'us-react-components/dist/utils/date';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { FacebookSdk } from 'us-react-components/dist/utils/FacebookSdk';

import { FacebookUtil } from '../../utils/FacebookUtil';

declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: {
          initialize: (opts: unknown) => void;
          renderButton: (el: HTMLElement | null, opts: unknown) => void;
        };
      };
    };
  }
}

const parseJwt = (token: string): { sub?: string } | null => {
  try {
    const payload = token.split('.')[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
};

interface SitterPlan {
  id?: number;
  userId?: number;
  recurringFee?: number;
  end?: string;
  cancelAt?: string;
  plan?: { id?: number; name?: string; frequency?: string };
}

interface NotificationSettings {
  weeklySummaryMail?: boolean;
  availabilityRemindText?: boolean;
  jobRequestText?: boolean;
  interviewRequestText?: boolean;
  accountNotificationText?: boolean;
  jobApplication?: boolean;
}

export const Settings = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const { id: pathId } = useParams<{ id: string }>();
  const { stores, services: { config } } = useDependencies();
  const user = stores.user.user;
  const sitter = stores.sitter.sitter as (typeof stores.sitter.sitter) & {
    phone?: string;
    acceptsAnyTime?: boolean;
    acceptsNewFamilies?: boolean;
    jobPostMessageEnabled?: boolean;
    hidden?: boolean;
  };

  const [cancelMembershipOpen, setCancelMembershipOpen] = useState(false);
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [email, setEmail] = useState<string>(user?.email ?? '');
  const [phone, setPhone] = useState<string>(sitter?.phone ?? '');
  const [changingPassword, setChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [currentPlan, setCurrentPlan] = useState<SitterPlan | null>(null);
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>({});
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});

  const toggleSection = (key: string): void => {
    setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    document.title = 'UrbanSitter - Account Settings';
  }, []);

  useEffect(() => {
    if (user?.email) setEmail(user.email);
  }, [user?.email]);

  useEffect(() => {
    if (sitter?.phone) setPhone(sitter.phone);
  }, [sitter?.phone]);

  useEffect(() => {
    const userId = user?.id;
    if (!userId || user?.googleUserId) return undefined;
    const clientId = config?.GOOGLE_APP_ID;
    if (!clientId) return undefined;

    const linkGoogle = (response: { credential?: string }): void => {
      if (!response.credential) {
        toast.error(t('pages.settings.google.linkFailed'));
        return;
      }
      const jwt = parseJwt(response.credential);
      if (!jwt?.sub) {
        toast.error(t('pages.settings.google.linkFailed'));
        return;
      }
      void (async (): Promise<void> => {
        try {
          await GoogleConnectionService.create({
            userId,
            googleUser: {
              accessToken: response.credential ?? '',
              googleId: jwt.sub ?? '',
              userId,
            },
          });
          toast.success(t('pages.settings.google.linkSuccess'));
          void stores.user.refreshUser?.();
        } catch {
          toast.error(t('pages.settings.google.linkError'));
        }
      })();
    };

    let cancelled = false;
    const render = (): void => {
      if (cancelled) return;
      const el = document.getElementById('link-google-sitter');
      if (!el || !window.google?.accounts?.id) return;
      window.google.accounts.id.initialize({
        auto_select: false,
        callback: linkGoogle,
        client_id: clientId,
      });
      window.google.accounts.id.renderButton(el, {
        shape: 'pill',
        size: 'large',
        text: 'continue_with',
        theme: 'outline',
        use_fedcm_for_button: true,
      });
    };

    if (window.google?.accounts?.id) {
      render();
      return (): void => {
        cancelled = true;
      };
    }
    const interval = window.setInterval(() => {
      if (window.google?.accounts?.id) {
        window.clearInterval(interval);
        render();
      }
    }, 200);
    return (): void => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [user?.id, user?.googleUserId, config?.GOOGLE_APP_ID, stores.user]);

  useEffect(() => {
    if (!user?.id) return;
    const loadCurrentPlan = async (): Promise<void> => {
      try {
        const resp = await UserPlanService.getByFilter({ params: { userId: user.id, active: true } });
        const plans = (resp.data?.data ?? []) as SitterPlan[];
        setCurrentPlan(plans[0] ?? null);
      } catch {
        setCurrentPlan(null);
      }
    };
    const loadNotifs = async (): Promise<void> => {
      try {
        if (!user?.id) return;

        const resp = await SitterNotificationSettingsService.getById(user.id);
        const settings = (resp.data?.data ?? {}) as NotificationSettings;
        setNotificationSettings(settings);
      } catch {
        setNotificationSettings({});
      }
    };
    void loadCurrentPlan();
    void loadNotifs();
  }, [user?.id]);

  const pid = Number(pathId);
  if (pid && user?.id && pid !== user.id) {
    navigate(`/sitter/${user.id}/settings`, { replace: true });
  }

  const saveAccount = async (): Promise<void> => {
    if (!user?.id || saving) return;
    if (changingPassword) {
      if (!newPassword || newPassword !== confirmPassword) {
        toast.error(t('components.passwordChange.passwordMismatch'));
        return;
      }
      if (!ValidationService.isValidPassword(newPassword)) {
        const failures = ValidationService.passwordFailures(newPassword);
        const requirementsTitle = t('components.passwordChange.requirements');
        if (failures.length === 0) {
          toast.error(requirementsTitle);
        } else {
          const lines = failures.map((key) =>
            t(`components.passwordChange.failures.${key}`),
          );
          toast.error(
            `${requirementsTitle}\n${lines.map((l) => `• ${l}`).join('\n')}`,
            { style: { whiteSpace: 'pre-line' } },
          );
        }
        return;
      }
    }
    setSaving(true);
    try {
      const body: {
        id: number;
        email?: string;
        password?: string;
        currentPassword?: string;
      } = { id: user.id };
      if (email && email !== user.email) body.email = email;
      if (changingPassword && newPassword) {
        body.password = newPassword;
        if (user.passwordChanged) body.currentPassword = currentPassword;
      }
      await UserService.patch(user.id, body as never);

      if (sitter?.id && phone !== sitter.phone) {
        await SitterService.patch(sitter.id, { ...sitter, phone } as never);
        await stores.sitter.refreshSitter?.();
      }

      await stores.user.refreshUser?.();
      toast.success(t('pages.settings.savedToast'));
      setChangingPassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setMode('view');
    } catch {
      toast.error(t('pages.settings.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const patchUser = async (patch: Record<string, unknown>): Promise<void> => {
    if (!user?.id) return;
    try {
      await UserService.patch(user.id, { id: user.id, ...patch } as never);
      await stores.user.refreshUser?.();
    } catch {
      toast.error(t('pages.settings.saveError'));
    }
  };

  const patchSitter = async (patch: Record<string, unknown>): Promise<void> => {
    if (!sitter?.id) return;
    try {
      await SitterService.patch(sitter.id, { ...sitter, ...patch } as never);
      await stores.sitter.refreshSitter?.();
    } catch {
      toast.error(t('pages.settings.saveError'));
    }
  };

  const patchNotifications = async (patch: Partial<NotificationSettings>): Promise<void> => {
    if (!sitter?.id || !SitterNotificationSettingsService.patch) {
      setNotificationSettings((prev) => ({ ...prev, ...patch }));
      return;
    }
    setNotificationSettings((prev) => ({ ...prev, ...patch }));
    try {
      await SitterNotificationSettingsService.patch(sitter.id, {
        ...notificationSettings,
        ...patch,
      } as never);
    } catch {
      toast.error(t('pages.settings.saveError'));
    }
  };

  if (!user?.id) {
    return (
      <div className='container py-8'>
        <i className='fa-solid fa-spinner fa-spin mr-2' />
        <span>{t('common.loading')}</span>
      </div>
    );
  }

  const isCanceled = !!currentPlan?.cancelAt;
  const isRecurring = !!currentPlan?.plan?.frequency;
  const canRenew = !!currentPlan?.end
    && !isRecurring
    && new Date(currentPlan.end).getTime() - Date.now() <= 30 * 24 * 60 * 60 * 1000;
  const profileStatus = sitter?.hidden
    ? t('pages.settings.profileStatusHidden')
    : t('pages.settings.profileStatusApproved');
  const isPaywallTester = stores.user.inTestGroup?.(tests.CAREGIVER_PAYWALL_STRIPE_PAYMENT) ?? false;
  const addPlanPath = isPaywallTester
    ? '/app/caregiver/buy-background-check'
    : '/app/buy-background-check';

  return (
    <div className='container py-6 max-w-5xl mx-auto'>
      <h1 className='text-2xl font-semibold mb-5'>{t('pages.settings.accountSettingsHeading')}</h1>

      <div id='account-settings-basics' className='border border-neutral-200 rounded shadow-sm bg-white mb-5 overflow-hidden'>
        <div className='flex items-center justify-between bg-neutral-50 px-5 py-3 border-b border-neutral-200'>
          <h2 className='text-lg font-semibold m-0'>{t('pages.settings.accountBasics')}</h2>
          <div className='flex items-center gap-3'>
            {mode === 'view' && (
              <Button
                buttonType={ButtonType.secondary}
                onClick={(): void => setMode('edit')}
              >
                {t('common.edit')}
              </Button>
            )}
            <Button
              buttonType={ButtonType.tertiary}
              aria-label={collapsedSections.basics ? t('pages.settings.expandSection') : t('pages.settings.collapseSection')}
              onClick={(): void => toggleSection('basics')}
              className='bg-transparent border-0 p-0 text-neutral-700'
            >
              <FontAwesomeIcon icon={collapsedSections.basics ? faChevronDown : faChevronUp} />
            </Button>
          </div>
        </div>
        <div className={`p-5 ${collapsedSections.basics ? 'hidden' : ''}`}>

        <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4'>
          <h4 className='font-semibold'>{t('common.name')}</h4>
          <div className='sm:col-span-2'>
            <div>{user.firstName} {user.lastName}</div>
            <div className='text-xs text-neutral-500'>
              {t('pages.settings.memberSince', { date: getLongDateString(user.created) })}
            </div>
          </div>
        </div>

        <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4'>
          <h4 className='font-semibold'>{t('pages.settings.rowHeadings.membershipPlan')}</h4>
          <div className='sm:col-span-2'>
            {!currentPlan ? (
              <span id='no-membership-plan'>
                {t('pages.settings.noneValue')} (
                <a href={addPlanPath} className='underline'>{t('pages.settings.addAPlan')}</a>
                )
              </span>
            ) : (
              <span id='membership-plan'>
                {currentPlan.plan?.name}
                {currentPlan.recurringFee && <> - ${currentPlan.recurringFee}</>}
              </span>
            )}
            {isCanceled && (
              <>
                {' - '}
                <span className='text-red-600'>{t('pages.settings.canceled')}</span>
              </>
            )}
            {currentPlan?.end
              && isCanceled
              && new Date(currentPlan.end) > new Date() && (
              <div className='mt-1'>
                {t('pages.settings.activeUntil', { date: getLongDateString(currentPlan.end) })}
              </div>
            )}
            {canRenew && (
              <div className='mt-1'>
                <a id='renew-membership' href={addPlanPath} className='underline'>
                  {t('pages.settings.renewMembershipPlan')}
                </a>
              </div>
            )}
            {!currentPlan?.cancelAt && currentPlan && (
              <div className='mt-1'>
                <Button
                  id='cancel-membership-plan'
                  buttonType={ButtonType.tertiary}
                  className='underline text-sm'
                  onClick={(): void => setCancelMembershipOpen(true)}
                >
                  {t('pages.settings.cancelMembershipPlan')}
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4'>
          <h4 className='font-semibold'>{t('pages.settings.rowHeadings.nextBillingDate')}</h4>
          <div id='billing-info' className='sm:col-span-2'>
            {currentPlan && !isCanceled && isRecurring ? getLongDateString(currentPlan.end) : t('pages.settings.noneValue')}
          </div>
        </div>

        <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4'>
          <h4 className='font-semibold'>{t('pages.settings.rowHeadings.profileStatus')}</h4>
          <div className='sm:col-span-2'>
            <span id='profile-status'>{profileStatus}</span>
          </div>
        </div>

        <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4'>
          <h4 className='font-semibold'>{t('pages.settings.rowHeadings.email')}</h4>
          <div className='sm:col-span-2'>
            {mode === 'view' ? (
              <span className='user-email'>{user.email}</span>
            ) : (
              <input
                id='account-settings-user-email'
                aria-label={t('pages.settings.emailAriaLabel')}
                className='form-control w-full border border-neutral-300 rounded px-3 py-2'
                type='email'
                value={email}
                onChange={(e): void => setEmail(e.target.value)}
              />
            )}
          </div>
        </div>

        <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4'>
          <h4 className='font-semibold'>{t('pages.settings.rowHeadings.password')}</h4>
          <div className='sm:col-span-2'>
            {mode === 'view' ? (
              user.passwordChanged ? (
                <div>
                  {t('pages.settings.updatedLine', {
                    date: typeof user.passwordChanged === 'string'
                      ? getLongDateString(user.passwordChanged)
                      : '',
                  })}
                </div>
              ) : (
                <Button
                  buttonType={ButtonType.tertiary}
                  id='create-password'
                  className='underline cursor-pointer bg-transparent border-0 p-0'
                  onClick={(): void => {
                    setMode('edit');
                    setChangingPassword(true);
                  }}
                >
                  {t('pages.settings.createPassword')}
                </Button>
              )
            ) : !changingPassword ? (
              <Button
                buttonType={ButtonType.tertiary}
                id='change-password'
                className='underline cursor-pointer bg-transparent border-0 p-0'
                onClick={(): void => setChangingPassword(true)}
              >
                {user.passwordChanged
                  ? t('pages.settings.changePassword')
                  : t('pages.settings.createPassword')}
              </Button>
            ) : (
              <PasswordChangeForm
                hasExistingPassword={!!user.passwordChanged}
                currentPassword={currentPassword}
                newPassword={newPassword}
                confirmPassword={confirmPassword}
                onCurrentPasswordChange={setCurrentPassword}
                onNewPasswordChange={setNewPassword}
                onConfirmPasswordChange={setConfirmPassword}
                onCancel={(): void => {
                  setChangingPassword(false);
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                }}
              />
            )}
          </div>
        </div>

        <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4'>
          <h4 className='font-semibold'>{t('pages.settings.rowHeadings.mobilePhone')}</h4>
          <div className='sm:col-span-2'>
            {mode === 'view' ? (
              <span>{sitter?.phone}</span>
            ) : (
              <>
                <input
                  id='user-phone-input'
                  className='form-control w-full border border-neutral-300 rounded px-3 py-2 mb-2'
                  type='tel'
                  maxLength={12}
                  placeholder={t('pages.settings.phonePlaceholder')}
                  value={phone}
                  onChange={(e): void => setPhone(e.target.value)}
                />
                <p className='italic text-sm'>
                  {t('pages.settings.phoneConsentPrefix')}{' '}
                  <a id='privacy-policy' href='/privacy-policy' target='_blank' rel='noreferrer' className='underline'>
                    {t('common.privacyPolicy')}
                  </a>
                  {t('pages.settings.phoneConsentSuffix')}
                </p>
              </>
            )}
          </div>
        </div>

        {mode === 'edit' && (
          <div className='flex justify-end gap-2 mt-4'>
            <Button buttonType={ButtonType.secondary} onClick={(): void => setMode('view')} disabled={saving}>
              {t('pages.settings.cancelChanges')}
            </Button>
            <Button buttonType={ButtonType.primary} onClick={saveAccount} disabled={saving}>
              {t('pages.settings.saveChanges')}
            </Button>
          </div>
        )}
        </div>
      </div>

      {/* Communication */}
      <div id='account-settings-communications' className='border border-neutral-200 rounded shadow-sm bg-white mb-5 overflow-hidden'>
        <div className='flex items-center justify-between bg-neutral-50 px-5 py-3 border-b border-neutral-200'>
          <h2 className='text-lg font-semibold m-0'>{t('pages.settings.communication')}</h2>
          <Button
            buttonType={ButtonType.tertiary}
            aria-label={collapsedSections.communication ? t('pages.settings.expandSection') : t('pages.settings.collapseSection')}
            onClick={(): void => toggleSection('communication')}
            className='bg-transparent border-0 p-0 text-neutral-700'
          >
            <FontAwesomeIcon icon={collapsedSections.communication ? faChevronDown : faChevronUp} />
          </Button>
        </div>
        <div className={`p-5 ${collapsedSections.communication ? 'hidden' : ''}`}>

        <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6'>
          <h4 className='font-semibold'>{t('pages.settings.rowHeadings.notifications')}</h4>
          <div className='sm:col-span-2 space-y-2'>
            <Checkbox
              className='flex items-start'
              inputClassName='mt-0.5 shrink-0'
              id='account-settings-monthly-newsletters'
              label={t('pages.settings.notifications.monthlyNewsletter')}
              checked={!!user.emailNewsletter}
              onChange={(): Promise<void> => patchUser({ emailNewsletter: !user.emailNewsletter })}
            />
            <Checkbox
              className='flex items-start'
              inputClassName='mt-0.5 shrink-0'
              id='account-settings-weekly-summary'
              label={t('pages.settings.notifications.weeklySummary')}
              checked={!!notificationSettings.weeklySummaryMail}
              onChange={(): Promise<void> =>
                patchNotifications({ weeklySummaryMail: !notificationSettings.weeklySummaryMail })
              }
            />
            <Checkbox
              className='flex items-start'
              inputClassName='mt-0.5 shrink-0'
              id='account-settings-job-post-message-enabled'
              label={t('pages.settings.notifications.jobPostAlerts')}
              checked={!!sitter?.jobPostMessageEnabled}
              onChange={(): Promise<void> =>
                patchSitter({ jobPostMessageEnabled: !sitter?.jobPostMessageEnabled })
              }
            />
            <Checkbox
              className='flex items-start'
              inputClassName='mt-0.5 shrink-0'
              id='account-settings-weekend-reminder'
              label={t('pages.settings.notifications.weekendReminder')}
              checked={!!notificationSettings.availabilityRemindText}
              onChange={(): Promise<void> =>
                patchNotifications({
                  availabilityRemindText: !notificationSettings.availabilityRemindText,
                })
              }
            />
            <Checkbox
              className='flex items-start'
              inputClassName='mt-0.5 shrink-0'
              id='account-settings-send-text-message-for-job-request'
              label={t('pages.settings.notifications.jobRequestText')}
              checked={!!notificationSettings.jobRequestText}
              onChange={(): Promise<void> =>
                patchNotifications({ jobRequestText: !notificationSettings.jobRequestText })
              }
            />
            <Checkbox
              className='flex items-start'
              inputClassName='mt-0.5 shrink-0'
              id='account-settings-send-text-message-for-interview-request'
              label={t('pages.settings.notifications.interviewRequestText')}
              checked={!!notificationSettings.interviewRequestText}
              onChange={(): Promise<void> =>
                patchNotifications({ interviewRequestText: !notificationSettings.interviewRequestText })
              }
            />
            <Checkbox
              className='flex items-start'
              inputClassName='mt-0.5 shrink-0'
              id='account-notification-text'
              label={t('pages.settings.notifications.accountNotificationText')}
              checked={!!notificationSettings.accountNotificationText}
              onChange={(): Promise<void> =>
                patchNotifications({ accountNotificationText: !notificationSettings.accountNotificationText })
              }
            />
            <Checkbox
              className='flex items-start'
              inputClassName='mt-0.5 shrink-0'
              id='job-post-application-notification'
              label={t('pages.settings.notifications.jobApplication')}
              checked={!!notificationSettings.jobApplication}
              onChange={(): Promise<void> =>
                patchNotifications({ jobApplication: !notificationSettings.jobApplication })
              }
            />
          </div>
        </div>

        <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6'>
          <h4 className='font-semibold'>{t('pages.settings.rowHeadings.bookingRequests')}</h4>
          <div className='sm:col-span-2 space-y-2'>
            <span className='flex items-start gap-3'>
              <input
                type='radio'
                name='booking-request-notification'
                id='booking-request-notification-any-timeslot'
                className='mt-0.5 shrink-0 border-solid border-gray-900 text-sapphire focus:border-sapphire focus:ring-0 focus:ring-offset-0 focus:outline-none focus:shadow-[0_1px_5px] focus:shadow-sapphire-300/100'
                checked={!!sitter?.acceptsAnyTime}
                onChange={(): Promise<void> => patchSitter({ acceptsAnyTime: true })}
              />
              <label htmlFor='booking-request-notification-any-timeslot'>
                {t('pages.settings.bookingRequests.anyTimeSlot')}
              </label>
            </span>
            <span className='flex items-start gap-3'>
              <input
                type='radio'
                name='booking-request-notification'
                id='booking-request-notification-available-timeslot'
                className='mt-0.5 shrink-0 border-solid border-gray-900 text-sapphire focus:border-sapphire focus:ring-0 focus:ring-offset-0 focus:outline-none focus:shadow-[0_1px_5px] focus:shadow-sapphire-300/100'
                checked={sitter?.acceptsAnyTime === false}
                onChange={(): Promise<void> => patchSitter({ acceptsAnyTime: false })}
              />
              <label htmlFor='booking-request-notification-available-timeslot'>
                {t('pages.settings.bookingRequests.onlyAvailableTimes')}
              </label>
            </span>
          </div>
        </div>

        <div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
          <h4 className='font-semibold'>{t('pages.settings.rowHeadings.newFamilies')}</h4>
          <div className='sm:col-span-2'>
            <Checkbox
              className='flex items-start'
              inputClassName='mt-0.5 shrink-0'
              id='account-settings-accepting-new-families'
              label={t('pages.settings.newFamilies.notAccepting')}
              checked={!sitter?.acceptsNewFamilies}
              onChange={(): Promise<void> =>
                patchSitter({ acceptsNewFamilies: !sitter?.acceptsNewFamilies })
              }
            />
          </div>
        </div>
        </div>
      </div>

      {/* Social Connections */}
      <div id='social-accounts' className='border border-neutral-200 rounded shadow-sm bg-white mb-5 overflow-hidden'>
        <div className='flex items-center justify-between bg-neutral-50 px-5 py-3 border-b border-neutral-200'>
          <h2 className='text-lg font-semibold m-0'>{t('pages.settings.socialConnections')}</h2>
          <Button
            buttonType={ButtonType.tertiary}
            aria-label={collapsedSections.social ? t('pages.settings.expandSection') : t('pages.settings.collapseSection')}
            onClick={(): void => toggleSection('social')}
            className='bg-transparent border-0 p-0 text-neutral-700'
          >
            <FontAwesomeIcon icon={collapsedSections.social ? faChevronDown : faChevronUp} />
          </Button>
        </div>
        <div className={`p-5 ${collapsedSections.social ? 'hidden' : ''}`}>
        <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4'>
          <h4 className='font-semibold'>{t('pages.settings.rowHeadings.facebook')}</h4>
          <div className='sm:col-span-2'>
            {user.facebookId ? (
              <span>{t('common.connected')}</span>
            ) : (
              <>
                <p className='mb-2'>{t('pages.settings.facebookConnectBody')}</p>
                <Button
                  id='connect-facebook'
                  buttonType={ButtonType.facebook}
                  onClick={(): void => {
                    void (async (): Promise<void> => {
                      try {
                        const sdk = await FacebookSdk.ensureReady();

                        sdk.login(
                          (response): void => {
                            FacebookUtil.linkFacebook(
                              response,
                              stores.user,
                              t,
                              () => stores.user.refreshUser(),
                            );
                          },
                          { scope: 'public_profile, email, user_friends' },
                        );
                      } catch {
                        toast.error(t('pages.settings.facebookSdkUnavailable'));
                      }
                    })();
                  }}
                >
                  <FontAwesomeIcon icon={faFacebook} className='mr-2 text-lg align-middle' />
                  {t('pages.settings.connectFacebook')}
                </Button>
              </>
            )}
          </div>
        </div>
        <div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
          <h4 className='font-semibold'>{t('pages.settings.rowHeadings.google')}</h4>
          <div className='sm:col-span-2'>
            {user.googleUserId ? (
              <span>{t('common.connected')}</span>
            ) : (
              <div id='link-google-sitter' className='g_id_signin' />
            )}
          </div>
        </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={cancelMembershipOpen}
        onClose={(): void => setCancelMembershipOpen(false)}
        title={t('pages.settings.cancelMembershipConfirmTitle')}
        body={<p>{t('pages.settings.cancelMembershipBody')}</p>}
        confirmLabel={t('pages.settings.cancelNow')}
        cancelLabel={t('pages.settings.keepMembership')}
        cancelButtonType={ButtonType.secondary}
        onConfirm={async (): Promise<void> => {
          if (!currentPlan?.id) {
            window.location.href = '/app/sitter-cancel-reasons';
            return;
          }
          try {
            await SitterPlanService.patch(currentPlan.id, {
              id: currentPlan.id,
              userId: currentPlan.userId ?? user.id,
              cancel: true,
            });
            window.location.href = '/app/sitter-cancel-reasons';
          } catch {
            toast.error(t('pages.settings.saveError'));
          }
        }}
      />
    </div>
  );
});
